/**
 * Email verification page component
 * Allows users to verify their email with a 6-digit code
 */

import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { confirmSignUp, resendSignUpCode } from '@/services/auth';

const CODE_LENGTH = 6;

export function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = (location.state as { email?: string })?.email || '';

  const [email] = React.useState(emailFromState);
  const [code, setCode] = React.useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [resendSuccess, setResendSuccess] = React.useState(false);

  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for resend cooldown
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, '').slice(0, CODE_LENGTH);
        const newCode = [...code];
        digits.split('').forEach((digit, i) => {
          if (i < CODE_LENGTH) {
            newCode[i] = digit;
          }
        });
        setCode(newCode);
        // Focus last filled input or next empty one
        const lastFilledIndex = Math.min(digits.length, CODE_LENGTH - 1);
        inputRefs.current[lastFilledIndex]?.focus();
      });
    }
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');

    if (verificationCode.length !== CODE_LENGTH) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    if (!email) {
      setError('Email address is missing. Please go back to registration.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await confirmSignUp({
        email,
        confirmationCode: verificationCode,
      });
      // Navigate to login page on success
      navigate('/login', {
        state: { message: 'Email verified successfully! Please sign in.' },
      });
    } catch (err) {
      console.error('Verification error:', err);
      if (err instanceof Error) {
        if (err.message.includes('CodeMismatchException')) {
          setError('Invalid verification code. Please try again.');
        } else if (err.message.includes('ExpiredCodeException')) {
          setError('Verification code has expired. Please request a new one.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) {
      return;
    }

    if (!email) {
      setError('Email address is missing. Please go back to registration.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      // Resend verification code
      await resendSignUpCode({ email });
      setResendSuccess(true);
      setResendCooldown(60); // 60 second cooldown
      setCode(['', '', '', '', '', '']); // Clear the code inputs
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error('Resend error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle={
        <>
          We sent a verification code to{' '}
          <span className="font-semibold text-primary">{email}</span>
        </>
      }
    >
      <div className="rounded-lg border border-border-subtle bg-surface p-8 shadow-md">
          {/* Success Message */}
          {resendSuccess && (
            <div
              className="mb-6 rounded-md border border-success/20 bg-success-soft px-4 py-3"
              role="status"
              aria-live="polite"
            >
              <p className="text-sm text-success">
                Verification code sent successfully!
              </p>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div
              className="mb-6 rounded-md border border-danger/20 bg-danger-soft px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Code Input */}
          <div className="mb-6">
            <label className="mb-3 block text-sm font-medium text-secondary">
              Verification code
            </label>
            <div className="flex justify-between gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className={cn(
                    'h-14 w-full rounded-md border border-border-subtle bg-surface text-center text-xl font-bold text-primary transition-all duration-base ease-standard focus-visible:outline-none focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12',
                    error && 'border-danger focus-visible:border-danger focus-visible:ring-danger/12',
                    digit && 'border-brand/30 bg-brand-soft'
                  )}
                  aria-label={`Digit ${index + 1}`}
                  autoComplete="off"
                />
              ))}
            </div>
            <p className="mt-3 text-xs text-muted">
              Enter the 6-digit code from your email
            </p>
          </div>

          {/* Verify Button */}
          <Button
            onClick={handleVerify}
            variant="primary"
            size="lg"
            loading={isLoading}
            disabled={isLoading || code.join('').length !== CODE_LENGTH}
            className="w-full"
          >
            {isLoading ? 'Verifying...' : 'Verify email'}
          </Button>

          {/* Resend Code */}
          <div className="mt-6 text-center">
            <p className="text-sm text-secondary">
              Didn&apos;t receive the code?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading || resendCooldown > 0}
                className={cn(
                  'font-medium text-brand hover:text-brand-hover transition-colors',
                  (isLoading || resendCooldown > 0) &&
                    'cursor-not-allowed opacity-50'
                )}
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend code'}
              </button>
            </p>
          </div>
        </div>

      {/* Back to Login Link */}
      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>
      </div>
    </AuthLayout>
  );
}
